import React from "react";

import { ArrowLink } from "@brexhq/next/src/components/shared/Link/ArrowLink";
import { RebirthFooterLink } from "@brexhq/next/src/components/shared/Link/FooterLink";
import { InlineLink } from "@brexhq/next/src/components/shared/Link/InlineLink";
import { NavLink } from "@brexhq/next/src/components/shared/Link/NavLink";
import { PrimaryButtonLink } from "@brexhq/next/src/components/shared/Link/PrimaryButtonLink";
import { UnstyledLink } from "@brexhq/next/src/components/shared/Link/UnstyledLink";
import useAnalytics from "@brexhq/next/src/hooks/useAnalytics";
import useModuleContext from "@brexhq/next/src/hooks/useModuleContext";
import {
  InternalLinkClickedEvent,
  OutboundLinkClickedEvent,
} from "@brexhq/next/src/telemetry/analyticsEvents";
import { checkInternalLink } from "@brexhq/next/src/utils/links";

import { LinkModifierConsts, CombinedRebirthLinkProps } from "./types";

export const Link = React.forwardRef<
  HTMLAnchorElement,
  CombinedRebirthLinkProps
>(function RebirthLinkComponent(
  {
    label,
    to,
    noFollow,
    modifier = LinkModifierConsts.inline,
    component,
    section = "",
    children,
    preventDefault,
    size,
    margin,
    theme,
    color,
    prefetch,
    isFullWidth,
    trackingId,
    withTextAnimation,
    target: explicitTarget,
    rel: explicitRel,
    variant,
    icon,
    withIcon,
    iconColor,
    ...defaultProps
  },
  ref,
) {
  const { isInternalLink, link } = checkInternalLink(to);
  const { indexInPageBuilder, type } = useModuleContext();

  const analyticsEvent = isInternalLink
    ? new InternalLinkClickedEvent(
        component,
        section,
        modifier,
        label,
        link,
        type,
        indexInPageBuilder.overall,
        trackingId,
      )
    : new OutboundLinkClickedEvent(to);
  const analytics = useAnalytics();
  const trackEvent = () => analytics.trackEvent(analyticsEvent);

  const target = explicitTarget || (isInternalLink ? "_self" : "_blank");
  const rel =
    explicitRel ||
    (isInternalLink
      ? undefined
      : "noopener noreferrer" + (noFollow ? " nofollow" : ""));

  switch (modifier) {
    case LinkModifierConsts.inline:
    case LinkModifierConsts.legacyLink:
      return (
        <InlineLink
          ref={ref}
          to={to}
          onClick={trackEvent}
          label={label}
          variant={
            modifier === LinkModifierConsts.legacyLink ? "legacy" : undefined
          }
          target={target}
          rel={rel}
          prefetch={prefetch}
          {...defaultProps}
        />
      );

    case LinkModifierConsts.primary:
    case LinkModifierConsts.secondary:
    case LinkModifierConsts.outline:
      return (
        <PrimaryButtonLink
          ref={ref}
          to={to}
          size={size}
          onClick={trackEvent}
          label={label}
          margin={margin}
          theme={theme}
          modifier={modifier}
          target={target}
          rel={rel}
          prefetch={prefetch}
          isFullWidth={isFullWidth}
          icon={icon}
          withIcon={withIcon}
          iconColor={iconColor}
          {...defaultProps}
        />
      );

    case LinkModifierConsts.backArrow:
    case LinkModifierConsts.arrow:
      return (
        <ArrowLink
          ref={ref}
          preventDefault={preventDefault}
          size={size}
          to={to}
          label={label}
          onClick={trackEvent}
          margin={margin}
          color={color}
          target={target}
          rel={rel}
          prefetch={prefetch}
          direction={
            modifier === LinkModifierConsts.backArrow ? "left" : "right"
          }
          withTextAnimation={withTextAnimation}
          variant={variant}
          withIcon={withIcon}
          icon={icon}
          iconColor={iconColor}
          theme={theme}
          {...defaultProps}
        />
      );

    case LinkModifierConsts.footer:
      return (
        <RebirthFooterLink
          ref={ref}
          to={to}
          label={label}
          onClick={trackEvent}
          target={target}
          rel={rel}
          prefetch={prefetch}
          {...defaultProps}
        />
      );

    case LinkModifierConsts.nav:
      return (
        <NavLink
          ref={ref}
          to={to}
          label={label}
          onClick={trackEvent}
          target={target}
          rel={rel}
          prefetch={prefetch}
          {...defaultProps}
        />
      );
    case LinkModifierConsts.unstyled:
      return (
        <UnstyledLink
          ref={ref}
          to={to}
          label={label}
          onClick={trackEvent}
          margin={margin}
          {...defaultProps}
          target={target}
          rel={rel}
          prefetch={prefetch}
        >
          {children}
        </UnstyledLink>
      );
    default:
      return null;
  }
});
